/**
 * PDApp bytes-only file handoff (#605) — `papan.files.pickAndRead` /
 * `pickAndSave` and drive drag-and-drop.
 *
 * ONE copy of the option contract, the display filter and the host ceilings,
 * shared by the host picker (SPA), the handoff routes (API) and their tests.
 * The SDK validates the same shape locally (it bundles no workspace package
 * but `@papan/reconnect`) and the server re-validates here, so an app that
 * bypasses the SDK meets the same rules.
 *
 * The model, restated because every rule below leans on it: the pick IS the
 * consent and the transfer IS the read/save. The app receives a snapshot of
 * bytes and never a path, id, reference or handle. Design + decisions:
 * docs/plans/2026-09-18_PDAPP_FILE_HANDOFF.md.
 */
import { z } from 'zod'
import { HANDOFF_NAME_SPOOF, MAX_GLOB_LENGTH, isValidNameGlob, normalizeFilterExtension } from './handoff-filter.js'

// ─── Host ceilings ───────────────────────────────────────────────────────────

/**
 * Hard host ceilings. The effective cap of a transfer is `min(app-requested,
 * ceiling)` — an app may LOWER a cap, never raise it past these.
 *
 * Constants, not env vars: a per-host knob would need the five-file
 * `env-var-change` coupling, and nothing yet asks for one host to differ.
 */
export const HANDOFF_LIMITS = Object.freeze({
  /** Files in one transfer, counted AFTER folder expansion + filtering.
   *  Matches the drive's `MAX_OPERATION_ITEMS`. */
  maxFiles: 1000,
  /** Bytes in one transfer. Matches the upload body ceiling. */
  maxBytes: 2 * 1024 ** 3,
  /** Nodes (folders + files, before filtering) a folder expansion may visit
   *  before it refuses. Separate from `maxFiles` because a filter (`*.csv`)
   *  can discard most of what it visits — this is what keeps the WORK bounded
   *  when the transfer itself would be small. */
  walkBudget: 20_000,
  /** Default byte cap when the app asks for buffered content (`bytes` /
   *  `text`): the whole set lands in the app's memory. `stream` has none
   *  beyond `maxBytes`. The app may raise it up to `maxBytes`. */
  bufferedDefaultBytes: 100 * 1024 ** 2,
  /** Items a user may tick in one pick (selections, not expanded files). */
  maxSelection: 1000,
  /** Links (`.chain` / `.shared`) one pick may expand — each costs a mount
   *  resolve and a source-tenant read, not one node. Matches the drive's
   *  download (`MAX_EXPAND_POINTERS`). Past it the pick is too big to measure. */
  maxLinkExpansions: 200,
  /** How long a save's finish (scan + write, up to `maxBytes`) may hold the
   *  save undecided. Past it the save reads as failed and the finish will not
   *  start writing; the SDK waits a little longer than this, so both see the
   *  same end. */
  saveFinishLeaseMs: 10 * 60_000,
  /** Files one save (`pickAndSave([...])`) may hold — each is its own grant
   *  and upload, into the one folder the person chooses. */
  maxSaveFiles: 50,
})

/**
 * Is this one of an app's own handoff paths (`…/_papan/handoff/…`)? The URL —
 * the serve session in it, or a grant — is the capability, so these carry
 * their own CORS: the API's credentialed global CORS would refuse the
 * sandboxed app's `Origin: null`.
 */
export function isHandoffTransferPath(path: string): boolean {
  return /^\/(?:s\/[^/]+|ppn-app)\/_papan\/handoff\//.test(path)
}

/**
 * Is this a handoff BYTES path — an app fetching the bytes a person handed it
 * (`…/_papan/handoff/read/<grant>/<n>`, or voiding `…/read/<grant>`) or
 * uploading the bytes they chose to save (`…/_papan/handoff/save/<grant>`)?
 * Exactly those shapes: they get their own rate tier (no cookies, so each is
 * keyed by IP, and one transfer is many requests — each bounded by its
 * grant). Anything else under the prefix — the requests and drops, a path no
 * route answers — stays on the method tiers.
 */
export function isHandoffBytesPath(path: string): boolean {
  return /^\/(?:s\/[^/]+|ppn-app)\/_papan\/handoff\/(?:read\/[A-Za-z0-9_-]{43}(?:\/\d{1,5})?|save\/[A-Za-z0-9_-]{43})$/.test(path)
}

// ─── Options ─────────────────────────────────────────────────────────────────

export const HANDOFF_SELECT = ['file', 'folder', 'any'] as const
export type HandoffSelect = (typeof HANDOFF_SELECT)[number]

export const HANDOFF_AS = ['bytes', 'text', 'stream'] as const
export type HandoffAs = (typeof HANDOFF_AS)[number]

/** A DISPLAY filter, not a security gate: the picker shows/enables only
 *  matching files, and a folder import includes only matching descendants. */
export interface HandoffFilter {
  /** Media types; `type/*` matches a whole family. */
  mime?: string[]
  /** Extensions without the dot, case-insensitive; multi-dot allowed (`tar.gz`). */
  extensions?: string[]
  /** Glob over the file NAME (not the path): `*`, `?`, `[a-z]`, `[!…]`. Case-insensitive. */
  nameGlob?: string
}

const MIME_PATTERN = /^[a-z0-9][a-z0-9!#$&^_.+-]*\/(\*|[a-z0-9][a-z0-9!#$&^_.+-]*)$/
const EXTENSION_PATTERN = /^[a-z0-9][a-z0-9._+-]{0,31}$/
const MAX_FILTER_ENTRIES = 64
/** A name glob's length, which bounds its per-character cost (matchNameGlob). */

export const handoffFilterSchema = z
  .object({
    mime: z
      // 255: RFC 6838's type/subtype bound — the policy is stored and re-read.
      .array(z.string().trim().toLowerCase().max(255).regex(MIME_PATTERN, 'expected a media type like "text/csv" or "image/*"'))
      .max(MAX_FILTER_ENTRIES)
      .optional(),
    extensions: z
      .array(
        z
          .string()
          .transform(normalizeFilterExtension)
          .pipe(z.string().regex(EXTENSION_PATTERN, 'expected an extension like "csv" or ".csv"')),
      )
      .max(MAX_FILTER_ENTRIES)
      .optional(),
    nameGlob: z.string().min(1).max(MAX_GLOB_LENGTH).optional(),
  })
  .strict()

/** Raw read options as an app sends them (the SDK's public shape). */
export const handoffReadOptionsSchema = z
  .object({
    select: z.enum(HANDOFF_SELECT).default('file'),
    multiple: z
      .union([
        z.boolean(),
        z
          .object({
            min: z.number().int().min(1).optional(),
            max: z.number().int().min(1).optional(),
          })
          .strict(),
      ])
      .default(false),
    filter: handoffFilterSchema.optional(),
    as: z.enum(HANDOFF_AS).default('bytes'),
    maxBytes: z.number().int().positive().optional(),
    maxFiles: z.number().int().positive().optional(),
  })
  .strict()

export type HandoffReadOptionsInput = z.input<typeof handoffReadOptionsSchema>

/** The options after normalization — what the picker and the resolver act on. */
export interface HandoffReadPolicy {
  select: HandoffSelect
  /** How many items the user may tick. Single selection is `{ min: 1, max: 1 }`. */
  selection: { min: number; max: number }
  filter: HandoffFilter | null
  as: HandoffAs
  /** Effective caps: `min(app-requested, host ceiling)`. */
  limits: { maxBytes: number; maxFiles: number }
}

export class HandoffOptionsError extends Error {
  constructor(message: string) {
    super(message)
    this.name = 'HandoffOptionsError'
  }
}

/**
 * Validate + normalize raw read options. Throws `HandoffOptionsError` with a
 * developer-facing message; the routes map it to 400 and the SDK to a
 * `TypeError` before any request is made.
 */
export function normalizeHandoffReadOptions(raw: unknown): HandoffReadPolicy {
  const parsed = handoffReadOptionsSchema.safeParse(raw ?? {})
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    const where = issue?.path.length ? issue.path.join('.') : 'options'
    throw new HandoffOptionsError(`Invalid ${where}: ${issue?.message ?? 'invalid value'}`)
  }
  const opts = parsed.data

  let selection: { min: number; max: number }
  if (opts.multiple === false) selection = { min: 1, max: 1 }
  else if (opts.multiple === true) selection = { min: 1, max: HANDOFF_LIMITS.maxSelection }
  else {
    const min = opts.multiple.min ?? 1
    const max = Math.min(opts.multiple.max ?? HANDOFF_LIMITS.maxSelection, HANDOFF_LIMITS.maxSelection)
    if (min > max) throw new HandoffOptionsError(`Invalid multiple: min (${min}) is greater than max (${max})`)
    selection = { min, max }
  }

  const filter = normalizeFilter(opts.filter)
  if (filter?.nameGlob !== undefined && !isValidNameGlob(filter.nameGlob)) {
    throw new HandoffOptionsError('Invalid filter.nameGlob: unbalanced "[" or an empty class')
  }

  const byteDefault = opts.as === 'stream' ? HANDOFF_LIMITS.maxBytes : HANDOFF_LIMITS.bufferedDefaultBytes
  return {
    select: opts.select,
    selection,
    filter,
    as: opts.as,
    limits: {
      maxBytes: Math.min(opts.maxBytes ?? byteDefault, HANDOFF_LIMITS.maxBytes),
      maxFiles: Math.min(opts.maxFiles ?? HANDOFF_LIMITS.maxFiles, HANDOFF_LIMITS.maxFiles),
    },
  }
}

/** An empty filter (`{}`, `{ mime: [] }`) is no filter — collapse it to null. */
function normalizeFilter(f: HandoffFilter | undefined): HandoffFilter | null {
  if (!f) return null
  const out: HandoffFilter = {}
  if (f.mime?.length) out.mime = [...new Set(f.mime)]
  if (f.extensions?.length) out.extensions = [...new Set(f.extensions)]
  if (f.nameGlob !== undefined) out.nameGlob = f.nameGlob
  return Object.keys(out).length ? out : null
}

// ─── Save options (pickAndSave) ─────────────────────────────────────────────

/** Raw save options as an app sends them. The bytes' size travels beside
 *  them (the SDK knows it; the app does not declare it). */
const saveMimeSchema = z.string().trim().toLowerCase().max(255).regex(/^[a-z0-9][a-z0-9!#$&^_.+-]*\/[a-z0-9][a-z0-9!#$&^_.+-]*$/)

export const handoffSaveOptionsSchema = z
  .object({
    /** The name the picker proposes; the person may change it. */
    suggestedName: z.string().trim().min(1).max(255).refine((n) => !HANDOFF_NAME_SPOOF.test(n), 'a name may not hold hidden, direction or blank characters (spaces are fine, never four in a row)'),
    /** The content's media type, if the app knows it. */
    mime: saveMimeSchema.optional(),
    /** What the person may choose as the destination: a folder to save into
     *  (default), an existing file to replace, or either. */
    select: z.enum(HANDOFF_SELECT).default('folder'),
    /** Which existing files are offered as replace targets (display only). */
    filter: handoffFilterSchema.optional(),
  })
  .strict()

export type HandoffSaveOptionsInput = z.input<typeof handoffSaveOptionsSchema>

/** Several files at once (`pickAndSave([...])`): the person chooses a FOLDER,
 *  and each file is saved there under its own name. Their sizes travel beside
 *  them, one per file, in the same order. */
export const handoffSaveManyOptionsSchema = z
  .object({
    files: z
      .array(z.object({
        name: z.string().trim().min(1).max(255).refine((n) => !HANDOFF_NAME_SPOOF.test(n), 'a name may not hold hidden, direction or blank characters (spaces are fine, never four in a row)'),
        mime: saveMimeSchema.optional(),
      }).strict())
      .min(1)
      .max(HANDOFF_LIMITS.maxSaveFiles),
  })
  .strict()

/** One file of a save. */
export interface HandoffSaveFile {
  name: string
  mime: string | null
  size: number
}

/** A save request, normalized — what the picker renders and the grant checks. */
export interface HandoffSavePolicy {
  suggestedName: string
  mime: string | null
  select: HandoffSelect
  filter: HandoffFilter | null
  /** Bytes the app will upload; the upload must match it exactly. For
   *  several files, their total. */
  size: number
  /** Several files (`pickAndSave([...])`), each saved under its own name into
   *  the folder the person chooses; `select` is then `folder`. Absent for one
   *  file, which the person may also rename. */
  files?: HandoffSaveFile[]
}

export function normalizeHandoffSaveOptions(raw: unknown, size: unknown): HandoffSavePolicy {
  if (raw && typeof raw === 'object' && 'files' in raw) return normalizeSaveMany(raw, size)
  const parsed = handoffSaveOptionsSchema.safeParse(raw ?? {})
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    const where = issue?.path.length ? issue.path.join('.') : 'options'
    throw new HandoffOptionsError(`Invalid ${where}: ${issue?.message ?? 'invalid value'}`)
  }
  if (typeof size !== 'number' || !Number.isInteger(size) || size < 0) {
    throw new HandoffOptionsError('Invalid size: expected a whole number of bytes')
  }
  const filter = normalizeFilter(parsed.data.filter)
  if (filter?.nameGlob !== undefined && !isValidNameGlob(filter.nameGlob)) {
    throw new HandoffOptionsError('Invalid filter.nameGlob: unbalanced "[" or an empty class')
  }
  return {
    suggestedName: parsed.data.suggestedName,
    mime: parsed.data.mime ?? null,
    select: parsed.data.select,
    filter,
    size,
  }
}

function normalizeSaveMany(raw: unknown, sizes: unknown): HandoffSavePolicy {
  const parsed = handoffSaveManyOptionsSchema.safeParse(raw)
  if (!parsed.success) {
    const issue = parsed.error.issues[0]
    const where = issue?.path.length ? issue.path.join('.') : 'options'
    throw new HandoffOptionsError(`Invalid ${where}: ${issue?.message ?? 'invalid value'}`)
  }
  const { files } = parsed.data
  if (!Array.isArray(sizes) || sizes.length !== files.length || !sizes.every((n) => typeof n === 'number' && Number.isInteger(n) && n >= 0)) {
    throw new HandoffOptionsError('Invalid size: expected a whole number of bytes for each file')
  }
  // One folder takes each name once (the drive's names are exact: `README.md`
  // and `readme.md` are two files).
  const seen = new Set<string>()
  for (const f of files) {
    const key = f.name
    if (seen.has(key)) throw new HandoffOptionsError(`Invalid files: "${f.name}" appears twice`)
    seen.add(key)
  }
  const list = files.map((f, i) => ({ name: f.name, mime: f.mime ?? null, size: sizes[i] as number }))
  return {
    suggestedName: list[0].name,
    mime: list[0].mime,
    select: 'folder',
    filter: null,
    size: list.reduce((sum, f) => sum + f.size, 0),
    files: list,
  }
}

// ─── The display filter ──────────────────────────────────────────────────────
// One implementation, shared with the SDK's drop from the device: handoff-filter.ts.
export { HANDOFF_NAME_SPOOF, effectiveMime, isValidNameGlob, matchNameGlob, matchesHandoffFilter } from './handoff-filter.js'

// ─── Caps ────────────────────────────────────────────────────────────────────

/** A refused transfer. Never a partial set: `actual` is what was found — exact
 *  when the whole selection was measured, else the lower bound reached before
 *  the walk stopped (`atLeast: true`). */
export interface HandoffCapRefusal {
  ok: false
  reason: 'too_large' | 'too_many'
  limit: number
  actual: number
  atLeast?: true
}

/** Check a MEASURED selection against the effective caps. Count first: it is
 *  the cheaper one to fix (pick fewer) and the one a walk stops on. */
export function checkHandoffCaps(
  measured: { files: number; bytes: number; atLeast?: boolean },
  limits: { maxFiles: number; maxBytes: number },
): HandoffCapRefusal | null {
  const atLeast = measured.atLeast ? ({ atLeast: true } as const) : {}
  if (measured.files > limits.maxFiles) {
    return { ok: false, reason: 'too_many', limit: limits.maxFiles, actual: measured.files, ...atLeast }
  }
  if (measured.bytes > limits.maxBytes) {
    return { ok: false, reason: 'too_large', limit: limits.maxBytes, actual: measured.bytes, ...atLeast }
  }
  return null
}

// ─── Wire shapes ─────────────────────────────────────────────────────────────

/** The picker page tells the Papan host that framed it where it stands
 *  (`{ type, pid, status }`, same-origin only): `answered` / `cancelled` when
 *  it settles the request; for a save, `closed` once the person is done
 *  watching it (the page stays open while the app uploads). */
export const HANDOFF_FINISHED_MESSAGE = 'papan:handoff:finished'

export type HandoffPageStatus = 'answered' | 'cancelled' | 'closed'

/**
 * The drag data type a Papan drive drag carries for apps: a one-time token
 * the SPA registered as the person at drag start. An app it is dropped on
 * redeems it (through its host, or on pd.app the server) for the CONTENT of
 * what was dragged — never ids. `papan.files.isHandoffDrag` / `readDrop`.
 */
export const HANDOFF_DRAG_MIME = 'application/x-papan-handoff'

export type HandoffRequestKind = 'read' | 'save'
export type HandoffRequestStatus = 'pending' | 'answered' | 'cancelled'

/** What the picker page renders a request from — never its binding or answer. */
export type HandoffRequestView = (
  | { kind: 'read'; appName: string; status: HandoffRequestStatus; policy: HandoffReadPolicy }
  | { kind: 'save'; appName: string; status: HandoffRequestStatus; save: HandoffSavePolicy }
) & {
  /** The tenant the app runs in — present only for a member of it: the drive
   *  the picker opens in when nothing else names one. */
  homeTenantId?: string
}

/** Per-file facts the asker receives with a read grant — never an id. */
export interface HandoffFileFacts {
  name: string
  relPath: string
  mime: string
  size: number
}

export type HandoffAnswer =
  /** `folders`: the names of the folders picked, as the person saw them —
   *  what was chosen, which the files cannot say when a picked folder held
   *  nothing the filter kept. Names only; nothing here finds a file again.
   *  Absent from an older host, and when only files were picked. */
  | { kind: 'read'; grant: string; files: HandoffFileFacts[]; folders?: string[] }
  /** `grants`: one per file, in the request's order (several files); `grant`
   *  is the first — the whole answer for one file. */
  | { kind: 'save'; grant: string; grants?: string[] }

/** What the ASKER collects: the answer, or why there is none. */
export type HandoffOutcome =
  | { status: 'pending' }
  | { status: 'answered'; answer: HandoffAnswer }
  | { status: 'cancelled'; refusal?: HandoffCapRefusal }

/** Anything that is not a cap: the uniform, oracle-free refusal. */
export interface HandoffUnavailable {
  ok: false
  reason: 'unavailable'
}

/** The picker's measure of a choice — what a confirm would transfer, or why not. */
export type HandoffMeasure =
  | { ok: true; dryRun: true; files: number; bytes: number }
  | HandoffCapRefusal
  | HandoffUnavailable

/** One file of a read handoff as the app receives it (before the SDK fetches
 *  `url` and swaps it for `bytes` / `text` / `stream`). `url` is pick-scoped
 *  and short-lived; it names no file id. */
export interface HandoffFileEntry {
  name: string
  /** Path within the chosen root: a picked file's name, or
   *  `<chosenFolder>/<path within it>` for a folder's contents. */
  relPath: string
  mime: string
  size: number
  url: string
}

/**
 * What a folder is to a SAVE picker (server-computed; the write re-checks):
 * `selectable` — the person can create a file in it; `hallway` — they cannot,
 * but it leads to a folder they can (openable, not choosable); `none` —
 * neither (shown dimmed: openable, but nothing below takes a save).
 */
export type HandoffSaveTarget = 'selectable' | 'hallway' | 'none'

/** The save picker's disclosure for folders it shows (the open one included),
 *  by id — a HINT: the answer and the upload each re-check the destination. */
export interface HandoffSaveTargets {
  rows: Record<string, HandoffSaveTarget>
  /** The PERSON can save nowhere, and why — their own account's state, said
   *  up front (no folder would ever take the save, and Save would never be
   *  pressed to learn it). */
  refused?: 'read_only_account'
}

/** A proposed name in a destination: free, an existing file the person may
 *  replace, blocked — one answer for "exists and you can't replace it",
 *  "exists and you can't see it" and "you can't save here", so the picker is
 *  no oracle about any of them — or not a valid file name. */
export type HandoffNameProbe = 'free' | 'replaceable' | 'blocked' | 'invalid'

/**
 * Why a save cannot go ahead, for the PERSON in the picker — never the app,
 * which learns only `not_saved`. `exists` asks them to replace or rename;
 * `cant_save_here` is the uniform answer for no write access, a file they
 * may not replace, and one they cannot see.
 */
export type HandoffSaveRefusal =
  | 'exists'
  | 'cant_save_here'
  | 'invalid_name'
  | 'uploads_paused'
  | 'storage_full'
  | 'read_only_account'

/** How a save that went ahead ended badly: a refusal re-checked at the upload's
 *  end, the bytes refused (malware), the app giving up, or a fault. */
export type HandoffSaveFailure = HandoffSaveRefusal | 'rejected' | 'cancelled' | 'error'

/** The picker's answer to a save request. */
export type HandoffSaveResult = { ok: true } | { ok: false; reason: HandoffSaveRefusal }

/** Where a save stands, for the PERSON (the picker page) — never the app. */
export type HandoffSaveStatus = (
  | { state: 'waiting' }
  | { state: 'saving' }
  | { state: 'saved' }
  | { state: 'failed'; reason: HandoffSaveFailure }
) & {
  /** Several files: how many are saved, of how many. `failed` then means one
   *  or more were not (its `reason` the first), once none is still going. */
  count?: { saved: number; total: number }
}

/** The grants of a save answer, one per file. */
export function saveAnswerGrants(answer: { grant: string; grants?: string[] }): string[] {
  return answer.grants && answer.grants.length ? answer.grants : [answer.grant]
}
